import { useEffect, useState } from "react";
import {
  Archive,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  Check,
  FileCheck2,
  Files,
  FolderKanban,
  Grid3X3,
  Headphones,
  History,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Moon,
  Maximize2,
  Minimize2,
  RotateCcw,
  SlidersHorizontal,
  ShieldCheck,
  Sparkles,
  Sun,
  UserRound,
  Users,
  Workflow,
  X
} from "lucide-react";

const DEFAULT_DOCK_ITEMS = ["dashboard", "processes", "reports", "support", "teknoparkAI"];
const MAX_DOCK_ITEMS = 9;

const TAB_TITLES = {
  dashboard: "Genel Bakış",
  companies: "Firmalar",
  projects: "Projeler",
  monthlyProcesses: "Aylık Süreçler",
  mailTasks: "Mail Görevleri",
  exemptionDayReport: "Gün ve Süre Takibi",
  postDeclaration5746Check: "5746 Beyan Kontrolü",
  monthlyFinancialReports: "Aylık Mali Veriler",
  annualTaxObligations: "Yıllık Beyan ve Yükümlülükler",
  files: "Dosyalar",
  portalCredentials: "Portal Erişimleri",
  announcements: "Duyurular",
  support: "Destek Merkezi",
  teknoparkAI: "Teknopark AI",
  users: "Ekip ve Yetkiler",
  archived: "Arşiv",
  logs: "İşlem Geçmişi",
  backupStatus: "Yedek Takibi",
  profile: "Profilim"
};

function CustomerNavigation({
  activeTab,
  setActiveTab,
  canAssignCompanies,
  canMonitorBackups,
  isAdmin,
  onLogout,
  user,
  userData,
  themeMode,
  toggleThemeMode,
  selectedProcessProjectId,
  openProcessProject
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDockEditorOpen, setIsDockEditorOpen] = useState(false);
  const dockStorageKey = `argevioCustomerDock:${user?.uid || "default"}`;
  const dockExpandedStorageKey = `${dockStorageKey}:expanded`;
  const [isDockExpanded, setIsDockExpanded] = useState(() => {
    try {
      return localStorage.getItem(`argevioCustomerDock:${user?.uid || "default"}:expanded`) === "true";
    } catch {
      return false;
    }
  });
  const [selectedDockKeys, setSelectedDockKeys] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`argevioCustomerDock:${user?.uid || "default"}`) || "null");
      return Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_DOCK_ITEMS;
    } catch {
      return DEFAULT_DOCK_ITEMS;
    }
  });

  const tenantName =
    userData?.companyName ||
    userData?.firmaAdi ||
    userData?.tenantName ||
    "Müşteri Portalı";
  const displayName = userData?.name || user?.displayName || user?.email || "Kullanıcı";
  const initial = displayName.slice(0, 1).toLocaleUpperCase("tr-TR");
  const profilePhoto = userData?.photoURL || user?.photoURL || "";

  const openTab = (tabName) => {
    setActiveTab(tabName);
    setIsMenuOpen(false);
  };

  const openProcesses = () => {
    if (openProcessProject) {
      openProcessProject(selectedProcessProjectId);
    } else {
      setActiveTab("monthlyProcesses");
    }
    setIsMenuOpen(false);
  };

  const menuItems = [
    { key: "companies", label: "Firmalar", icon: Building2 },
    { key: "projects", label: "Projeler", icon: FolderKanban },
    { key: "exemptionDayReport", label: "Gün ve Süre Takibi", dockLabel: "Gün Takibi", icon: CalendarDays },
    { key: "postDeclaration5746Check", label: "5746 Beyan Kontrolü", dockLabel: "5746 Kontrol", icon: FileCheck2 },
    { key: "monthlyFinancialReports", label: "Aylık Mali Veriler", dockLabel: "Mali Veriler", icon: BarChart3 },
    { key: "annualTaxObligations", label: "Yıllık Beyan ve Yükümlülükler", dockLabel: "Yıllık Beyan", icon: Landmark },
    { key: "files", label: "Dosyalar", icon: Files },
    { key: "portalCredentials", label: "Portal Erişimleri", dockLabel: "Portallar", icon: KeyRound },
    { key: "announcements", label: "Duyurular", icon: Megaphone },
    { key: "profile", label: "Profilim", icon: UserRound },
    ...(canAssignCompanies ? [{ key: "users", label: "Ekip ve Yetkiler", dockLabel: "Ekip", icon: Users }] : []),
    { key: "archived", label: "Arşiv", icon: Archive },
    ...(isAdmin ? [{ key: "logs", label: "İşlem Geçmişi", dockLabel: "Geçmiş", icon: History }] : []),
    ...(canMonitorBackups ? [{ key: "backupStatus", label: "Yedek Takibi", dockLabel: "Yedekler", icon: ShieldCheck }] : [])
  ];

  const dockCatalog = [
    {
      key: "dashboard",
      label: "Genel Bakış",
      icon: LayoutDashboard,
      active: activeTab === "dashboard",
      onClick: () => openTab("dashboard")
    },
    {
      key: "processes",
      label: "Süreçler",
      icon: Workflow,
      active: ["monthlyProcesses", "mailTasks"].includes(activeTab),
      onClick: openProcesses
    },
    {
      key: "reports",
      label: "Raporlar",
      icon: BarChart3,
      active:
        ["monthlyFinancialReports", "annualTaxObligations", "exemptionDayReport", "postDeclaration5746Check"].includes(activeTab) &&
        !selectedDockKeys.includes(activeTab),
      onClick: () => openTab("monthlyFinancialReports")
    },
    {
      key: "support",
      label: "Destek",
      icon: Headphones,
      active: activeTab === "support",
      onClick: () => openTab("support")
    },
    {
      key: "teknoparkAI",
      label: "Teknopark AI",
      icon: Sparkles,
      active: activeTab === "teknoparkAI",
      onClick: () => openTab("teknoparkAI")
    },
    ...menuItems.map((item) => ({
      ...item,
      label: item.dockLabel || item.label,
      fullLabel: item.label,
      active: activeTab === item.key,
      onClick: () => openTab(item.key)
    }))
  ];
  const dockItems = selectedDockKeys
    .map((key) => dockCatalog.find((item) => item.key === key))
    .filter(Boolean);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(dockStorageKey) || "null");
      setSelectedDockKeys(Array.isArray(saved) && saved.length > 0 ? saved : DEFAULT_DOCK_ITEMS);
      setIsDockExpanded(localStorage.getItem(dockExpandedStorageKey) === "true");
    } catch {
      setSelectedDockKeys(DEFAULT_DOCK_ITEMS);
      setIsDockExpanded(false);
    }
  }, [dockExpandedStorageKey, dockStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(dockStorageKey, JSON.stringify(selectedDockKeys));
    } catch {
      // Cihaz tercihi saklanamıyorsa menü oturum boyunca çalışmaya devam eder.
    }
  }, [dockStorageKey, selectedDockKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(dockExpandedStorageKey, String(isDockExpanded));
    } catch {
      // Cihaz tercihi saklanamıyorsa menü oturum boyunca çalışmaya devam eder.
    }
  }, [dockExpandedStorageKey, isDockExpanded]);

  const toggleDockItem = (key) => {
    setSelectedDockKeys((current) => {
      if (current.includes(key)) {
        return current.length > 1 ? current.filter((item) => item !== key) : current;
      }

      if (current.length >= MAX_DOCK_ITEMS) {
        return current;
      }

      return [...current, key];
    });
  };

  const resetDock = () => {
    setSelectedDockKeys(DEFAULT_DOCK_ITEMS);
    setIsDockExpanded(false);
  };

  return (
    <>
      <header className="aurora-topbar">
        <button
          type="button"
          className="aurora-brand"
          onClick={() => openTab("dashboard")}
          aria-label="Genel bakışa dön"
        >
          <span className="aurora-brand-lockup">
            <img src="/argevio-flow-logo-light.svg" alt="Ar-Ge vio" />
            <small>{tenantName}</small>
          </span>
        </button>

        <div className="aurora-page-identity">
          <span>Çalışma alanı</span>
          <strong>{TAB_TITLES[activeTab] || "Argevio"}</strong>
        </div>

        <div className="aurora-topbar-actions">
          <button
            type="button"
            className="aurora-icon-button"
            onClick={() => setIsMenuOpen(true)}
            aria-label="Tüm uygulamaları aç"
            title="Tüm uygulamalar"
          >
            <Grid3X3 size={19} />
          </button>
          <button
            type="button"
            className="aurora-icon-button"
            onClick={toggleThemeMode}
            aria-label={themeMode === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
            title={themeMode === "dark" ? "Açık tema" : "Koyu tema"}
          >
            {themeMode === "dark" ? <Sun size={19} /> : <Moon size={19} />}
          </button>
          <button
            type="button"
            className="aurora-profile-button"
            onClick={() => openTab("profile")}
            title="Profilimi aç"
          >
            <span className="aurora-avatar">
              {profilePhoto ? <img src={profilePhoto} alt="Kullanıcı avatarı" /> : initial}
            </span>
            <span className="aurora-profile-copy">
              <strong>{displayName}</strong>
              <small>{userData?.role === "admin" ? "Admin" : "Kullanıcı"}</small>
            </span>
          </button>
        </div>
      </header>

      <nav className={`aurora-floating-dock${isDockExpanded ? " is-expanded" : ""}`} aria-label="Ana menü">
        {dockItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              className={`aurora-dock-button${item.active ? " is-active" : ""}`}
              onClick={item.onClick}
              aria-current={item.active ? "page" : undefined}
              aria-label={item.label}
              title={item.label}
            >
              <Icon size={20} />
              <span>{item.label}</span>
              <i aria-hidden="true" />
            </button>
          );
        })}
        <span className="aurora-dock-separator" aria-hidden="true" />
        <button
          type="button"
          className="aurora-dock-control"
          onClick={() => setIsDockExpanded((current) => !current)}
          aria-label={isDockExpanded ? "Menüyü daralt" : "Menüyü genişlet"}
          title={isDockExpanded ? "Menüyü daralt" : "Menüyü genişlet"}
        >
          {isDockExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
        <button
          type="button"
          className="aurora-dock-control"
          onClick={() => setIsDockEditorOpen(true)}
          aria-label="Alt menüyü düzenle"
          title="Alt menüyü düzenle"
        >
          <SlidersHorizontal size={18} />
        </button>
      </nav>

      {isDockEditorOpen && (
        <div className="aurora-menu-backdrop" onClick={() => setIsDockEditorOpen(false)}>
          <section
            className="aurora-app-menu aurora-dock-editor"
            role="dialog"
            aria-modal="true"
            aria-label="Alt menüyü düzenle"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="aurora-app-menu-head">
              <div>
                <span>Kişisel navigasyon</span>
                <strong>Alt menünüzü düzenleyin</strong>
              </div>
              <button type="button" onClick={() => setIsDockEditorOpen(false)} aria-label="Düzenleyiciyi kapat">
                <X size={20} />
              </button>
            </div>

            <p className="aurora-dock-editor-note">
              En sık kullandığınız en fazla {MAX_DOCK_ITEMS} alanı seçin. Seçimleriniz bu cihazda korunur.
              <strong>{selectedDockKeys.length} / {MAX_DOCK_ITEMS} seçili</strong>
            </p>

            <div className="aurora-dock-options">
              {dockCatalog.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedDockKeys.includes(item.key);
                const isDisabled = !isSelected && selectedDockKeys.length >= MAX_DOCK_ITEMS;

                return (
                  <button
                    key={item.key}
                    type="button"
                    className={isSelected ? "is-selected" : ""}
                    onClick={() => toggleDockItem(item.key)}
                    disabled={isDisabled}
                    aria-pressed={isSelected}
                  >
                    <span><Icon size={19} /></span>
                    <strong>{item.fullLabel || item.label}</strong>
                    <i>{isSelected ? <Check size={16} /> : null}</i>
                  </button>
                );
              })}
            </div>

            <div className="aurora-dock-editor-footer">
              <button type="button" onClick={resetDock}><RotateCcw size={16} /> Varsayılana dön</button>
              <button type="button" className="is-primary" onClick={() => setIsDockEditorOpen(false)}>Tamam</button>
            </div>
          </section>
        </div>
      )}

      {isMenuOpen && (
        <div className="aurora-menu-backdrop" onClick={() => setIsMenuOpen(false)}>
          <section
            className="aurora-app-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Tüm uygulamalar"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="aurora-app-menu-head">
              <div>
                <span>Argevio çalışma alanı</span>
                <strong>Tüm uygulamalar</strong>
              </div>
              <button type="button" onClick={() => setIsMenuOpen(false)} aria-label="Menüyü kapat">
                <X size={20} />
              </button>
            </div>

            <div className="aurora-app-grid">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={activeTab === item.key ? "is-active" : ""}
                    onClick={() => openTab(item.key)}
                  >
                    <span><Icon size={20} /></span>
                    <strong>{item.label}</strong>
                  </button>
                );
              })}
            </div>

            <div className="aurora-app-menu-footer">
              <span><Bell size={17} /> Bildirim ve mesajlarınıza sağ alt köşeden ulaşabilirsiniz.</span>
              <button type="button" onClick={onLogout}>
                <LogOut size={17} /> Çıkış Yap
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default CustomerNavigation;
